import { useState } from 'react';
import { ChevronUp, ChevronDown, Plus, Check, X } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/Button';
import { SearchableReasonSelect } from './SearchableReasonSelect';
import type { EntitySuggestion, EntityType, RedactionReason } from '../../types';
import { ENTITY_TYPE_CONFIG } from '../../types';
import { cn } from '../ui/utils';

interface ResultsPanelProps {
  suggestions: EntitySuggestion[];
  onApprove: (indices: number[]) => void;
  onReject: (indices: number[]) => void;
  onAddManualRedaction: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  isLoading?: boolean;
  onNavigate?: (suggestion: EntitySuggestion) => void;
  selectedSuggestionIndex?: number | null;
  reasons: RedactionReason[];
  entityReasons: Map<number, string>;
  onReasonChange: (entityIndex: number, reasonTitle: string) => void;
}

export function ResultsPanel({
  suggestions,
  onApprove,
  onReject,
  onAddManualRedaction,
  isCollapsed,
  onToggleCollapse,
  isLoading = false,
  onNavigate,
  selectedSuggestionIndex,
  reasons,
  entityReasons,
  onReasonChange,
}: ResultsPanelProps) {
  const [selectedType, setSelectedType] = useState<EntityType | 'all'>('all');
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());

  // Get unique entity types from suggestions
  const entityTypes = Array.from(new Set(suggestions.map((s) => s.entityType)));

  // Filter suggestions by type
  const filteredSuggestions = suggestions.filter((s) => {
    if (selectedType === 'all') return true;
    return s.entityType === selectedType;
  });

  // Pending suggestions (not yet approved/rejected)
  const pendingSuggestions = filteredSuggestions.filter((s) => s.status === 'pending');

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIndices(new Set(pendingSuggestions.map((s) => s.index)));
    } else {
      setSelectedIndices(new Set());
    }
  };

  const handleSelectOne = (index: number, checked: boolean) => {
    const newSet = new Set(selectedIndices);
    if (checked) {
      newSet.add(index);
    } else {
      newSet.delete(index);
    }
    setSelectedIndices(newSet);
  };

  const handleApproveSelected = () => {
    if (selectedIndices.size > 0) {
      onApprove(Array.from(selectedIndices));
      setSelectedIndices(new Set());
    }
  };

  const handleRejectSelected = () => {
    if (selectedIndices.size > 0) {
      onReject(Array.from(selectedIndices));
      setSelectedIndices(new Set());
    }
  };

  // Collapsed state
  if (isCollapsed) {
    return (
      <div className="h-10 border-t border-border bg-gray-50 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium">Results</span>
          <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">
            {suggestions.filter((s) => s.status === 'pending').length} pending
          </span>
        </div>
        <button
          onClick={onToggleCollapse}
          className="p-1 hover:bg-gray-200 rounded-md transition-colors"
          aria-label="Expand results panel"
        >
          <ChevronUp className="h-4 w-4 text-gray-600" />
        </button>
      </div>
    );
  }

  return (
    <div className="h-64 border-t border-border bg-white flex flex-col overflow-hidden">
      <Tabs defaultValue="results" className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-border shrink-0">
          <TabsList>
            <TabsTrigger value="results">Results</TabsTrigger>
            <TabsTrigger value="log">Redaction Log</TabsTrigger>
          </TabsList>
          <div className="flex items-center gap-2">
            <button
              onClick={onToggleCollapse}
              className="p-1 hover:bg-gray-100 rounded-md transition-colors"
              aria-label="Collapse results panel"
            >
              <ChevronDown className="h-4 w-4 text-gray-600" />
            </button>
            <Button variant="outline" size="sm" onClick={onAddManualRedaction}>
              <Plus className="h-3.5 w-3.5 mr-1" />
              Manual
            </Button>
          </div>
        </div>

        <TabsContent value="results" className="mt-0 flex-1 flex flex-col min-h-0 overflow-hidden">
            {/* Filter Chips */}
            <div className="flex items-center gap-2 px-4 py-2 border-b border-border overflow-x-auto shrink-0">
              <span className="text-xs text-gray-500 flex-shrink-0">PII Found ({suggestions.length})</span>
              <button
                onClick={() => setSelectedType('all')}
                className={cn(
                  'px-2.5 py-1 text-xs rounded-full transition-colors flex-shrink-0',
                  selectedType === 'all'
                    ? 'bg-[#7035C4] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                )}
              >
                All PIIs
              </button>
              {entityTypes.map((type) => {
                const config = ENTITY_TYPE_CONFIG[type];
                const count = suggestions.filter((s) => s.entityType === type).length;
                return (
                  <button
                    key={type}
                    onClick={() => setSelectedType(type)}
                    className={cn(
                      'px-2.5 py-1 text-xs rounded-full transition-colors flex-shrink-0 flex items-center gap-1.5',
                      selectedType === type
                        ? 'text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    )}
                    style={{
                      backgroundColor: selectedType === type ? config.color : undefined,
                    }}
                  >
                    {config.label}
                    <span className="opacity-75">({count})</span>
                  </button>
                );
              })}
            </div>

            {/* Bulk Actions */}
            {selectedIndices.size > 0 && (
              <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 border-b border-border">
                <span className="text-xs text-gray-600">
                  {selectedIndices.size} selected
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleApproveSelected}
                  className="h-7 text-xs"
                >
                  <Check className="h-3 w-3 mr-1" />
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleRejectSelected}
                  className="h-7 text-xs"
                >
                  <X className="h-3 w-3 mr-1" />
                  Reject
                </Button>
              </div>
            )}

            {/* Results Table */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {isLoading ? (
                <div className="flex items-center justify-center h-full text-gray-500 text-sm">
                  Loading suggestions...
                </div>
              ) : filteredSuggestions.length === 0 ? (
                <div className="flex items-center justify-center h-full text-gray-500 text-sm">
                  No PII detected
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="w-10 px-4 py-2 text-left">
                        <Checkbox
                          checked={
                            pendingSuggestions.length > 0 &&
                            pendingSuggestions.every((s) => selectedIndices.has(s.index))
                          }
                          onCheckedChange={(checked: boolean) => handleSelectAll(checked)}
                          aria-label="Select all"
                        />
                      </th>
                      <th className="px-4 py-2 text-left font-medium text-gray-600">Type</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-600">Value</th>
                      <th className="w-16 px-4 py-2 text-left font-medium text-gray-600">Page</th>
                      <th className="w-24 px-4 py-2 text-left font-medium text-gray-600">Confidence</th>
                      <th className="w-40 px-4 py-2 text-left font-medium text-gray-600">
                        Redaction Reason
                      </th>
                      <th className="w-20 px-4 py-2 text-left font-medium text-gray-600">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSuggestions.map((suggestion) => {
                      const config = ENTITY_TYPE_CONFIG[suggestion.entityType];
                      const isPending = suggestion.status === 'pending';
                      const isSelected = selectedSuggestionIndex === suggestion.index;
                      return (
                        <tr
                          key={suggestion.index}
                          className={cn(
                            'border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors',
                            !isPending && 'opacity-60',
                            isSelected && 'bg-[#7035C4]/10 hover:bg-[#7035C4]/15'
                          )}
                          onClick={() => onNavigate?.(suggestion)}
                        >
                          <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                            {isPending && (
                              <Checkbox
                                checked={selectedIndices.has(suggestion.index)}
                                onCheckedChange={(checked: boolean | 'indeterminate') =>
                                  handleSelectOne(suggestion.index, checked === true)
                                }
                                aria-label={`Select ${suggestion.text}`}
                              />
                            )}
                          </td>
                          <td className="px-4 py-2">
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                              style={{
                                backgroundColor: `${config.color}20`,
                                color: config.color,
                              }}
                            >
                              {config.label}
                            </span>
                          </td>
                          <td className="px-4 py-2 font-mono text-xs truncate max-w-[200px]" title={suggestion.text}>
                            {suggestion.text}
                          </td>
                          <td className="px-4 py-2 text-gray-600">{suggestion.page + 1}</td>
                          <td className="px-4 py-2">
                            <span
                              className={cn(
                                'text-xs',
                                suggestion.confidence >= 0.9 && 'text-green-600',
                                suggestion.confidence >= 0.7 && suggestion.confidence < 0.9 && 'text-yellow-600',
                                suggestion.confidence < 0.7 && 'text-red-600'
                              )}
                            >
                              {Math.round(suggestion.confidence * 100)}%
                            </span>
                          </td>
                          <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                            {isPending ? (
                              reasons.length > 0 ? (
                                <SearchableReasonSelect
                                  reasons={reasons}
                                  value={entityReasons.get(suggestion.index) || ''}
                                  onChange={(value) => onReasonChange(suggestion.index, value)}
                                  placeholder="Select reason..."
                                />
                              ) : (
                                <span className="text-xs text-gray-400 italic">No reasons configured</span>
                              )
                            ) : (
                              <span className="text-xs text-gray-500">
                                {/* For approved/rejected, prefer local entityReasons, then replacementText from backend */}
                                {entityReasons.get(suggestion.index) || suggestion.replacementText || '-'}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2">
                            <span
                              className={cn(
                                'text-xs font-medium',
                                suggestion.status === 'approved' && 'text-green-600',
                                suggestion.status === 'rejected' && 'text-red-600',
                                suggestion.status === 'pending' && 'text-amber-600'
                              )}
                            >
                              {suggestion.status.charAt(0).toUpperCase() + suggestion.status.slice(1)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>

        <TabsContent value="log" className="mt-0 flex-1 min-h-0 overflow-hidden">
          <div className="flex items-center justify-center h-full text-gray-500 text-sm">
            Redaction log will appear here
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
